import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { Clock, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Documentazione su UMIFUL, consultabile.
 *
 * Non è un percorso formativo: niente progressi, niente quiz, niente badge.
 * Chi legge è un professionista e decide da sé cosa gli serve e in che ordine.
 */
export default function KnowUmifulPage() {
  const [modules, setModules] = useState([]);
  const [fullModule, setFullModule] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/learning/modules`);
      setModules(resp.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openModule = async (moduleId) => {
    try {
      const resp = await axios.get(`${API}/learning/modules/${moduleId}`);
      setFullModule(resp.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error('Non è stato possibile aprire la scheda'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#12100c]" /></div>;

  // Scheda aperta
  if (fullModule) {
    const isObiezioni = Array.isArray(fullModule.objections) && fullModule.objections.length > 0;
    return (
      <div className="max-w-3xl" data-testid={`module-detail-${fullModule.id}`}>
        <button onClick={() => setFullModule(null)}
          className="text-sm text-[#4a4238] hover:text-[#12100c] mb-4 flex items-center gap-1" data-testid="module-back-btn">
          &larr; Torna all'elenco
        </button>

        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">{fullModule.icon}</span>
          <div>
            <Badge className="text-[10px] mb-1" style={{ backgroundColor: fullModule.color + '15', color: fullModule.color }}>
              {fullModule.tag}
            </Badge>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#12100c]">{fullModule.title}</h1>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6 text-xs text-[#4a4238]">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fullModule.reading_time} min di lettura</span>
        </div>

        {isObiezioni ? (
          <div className="space-y-4">
            {fullModule.objections.map((obj, i) => (
              <Card key={i} className="rounded-2xl border-gray-100 shadow-sm overflow-hidden">
                <CardContent className="p-0">
                  <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                    <p className="font-semibold text-sm text-[#12100c]">Obiezione: "{obj.title}"</p>
                  </div>
                  <div className="px-5 py-4 border-l-4 border-[#c6a15b] bg-[#c6a15b]/[0.04]">
                    <p className="text-xs font-semibold text-[#8a6620] uppercase tracking-wide mb-1">Come si risponde</p>
                    <p className="text-sm text-[#4a4238] leading-relaxed italic">"{obj.response}"</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {fullModule.sections?.map((sec, i) => (
              <Card key={i} className="rounded-2xl border-gray-100 shadow-sm">
                <CardContent className="p-5 sm:p-6">
                  <h3 className="font-semibold text-[#12100c] mb-3">{sec.title}</h3>
                  <div className="text-sm text-[#4a4238] leading-relaxed whitespace-pre-line prose-content">
                    {renderContent(sec.content)}
                  </div>
                </CardContent>
              </Card>
            ))}

            {fullModule.faqs?.length > 0 && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-[#12100c] mb-3">Domande frequenti</h3>
                <Accordion type="single" collapsible className="space-y-2">
                  {fullModule.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border border-gray-100 rounded-xl bg-white">
                      <AccordionTrigger className="px-4 py-3 text-sm font-medium text-[#12100c] hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-3 text-sm text-[#4a4238] leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-end">
          {fullModule.id < modules.length && (
            <Button variant="ghost" onClick={() => openModule(fullModule.id + 1)} className="text-[#4a5f43]" data-testid="next-module-btn">
              Scheda successiva <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Elenco
  return (
    <div className="max-w-3xl" data-testid="know-umiful-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Conosci UMIFUL</h1>
        <p className="text-sm text-[#4a4238] mt-2 max-w-2xl leading-relaxed">
          Documentazione sul prodotto, da consultare quando serve. Ogni scheda si legge per conto suo.
        </p>
      </div>

      <div className="space-y-3">
        {modules.map((mod) => (
          <button key={mod.id} onClick={() => openModule(mod.id)} className="w-full text-left" data-testid={`module-card-${mod.id}`}>
            <Card className="rounded-2xl shadow-sm border-gray-100 transition-all hover:shadow-md hover:-translate-y-0.5">
              <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-2xl"
                  style={{ backgroundColor: mod.color + '10' }}>
                  {mod.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className="text-[9px] border-gray-200">{mod.tag}</Badge>
                    <span className="text-[10px] text-[#4a4238] flex items-center gap-1"><Clock className="w-3 h-3" /> {mod.reading_time} min</span>
                  </div>
                  <h3 className="font-semibold text-sm text-[#12100c] truncate">{mod.title}</h3>
                </div>
                <ChevronRight className="w-5 h-5 text-[#4a4238] shrink-0" />
              </CardContent>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}

function renderContent(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    // Grassetto
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const rendered = parts.map((p, j) => j % 2 === 1 ? <strong key={j} className="text-[#12100c]">{p}</strong> : p);
    // Elenco puntato
    if (line.startsWith('- ')) return <div key={i} className="flex gap-2 mt-1"><span className="text-[#4a5f43] mt-0.5">&#x2022;</span><span>{rendered.map((r) => typeof r === 'string' ? r.replace(/^- /, '') : r)}</span></div>;
    // Riga di tabella
    if (line.startsWith('|')) return <div key={i} className="font-mono text-xs bg-gray-50 px-2 py-1 rounded">{line}</div>;
    return <p key={i} className="mt-1">{rendered}</p>;
  });
}
